/**
 * Month-end review ("Closing the books on [Month]") - data helpers + theme palettes.
 *
 * Everything here is worked out from data the app already stores, so every
 * past month has a review without anything extra being saved. The only thing
 * written back is the review state (seen / skipped) under userData.month_end_review
 * and any note pinned to next month's budget notes.
 *
 * Totals use exactly the same rules as BudgetTab / BudgetHero, so the numbers in a
 * review always match what the Budget tab showed for that month.
 */
import { parseCategoryEmoji } from "./categoryEmoji";
import { isPurpleOn } from "./purple";

export const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const NUMBER_WORDS = ['Zero', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen', 'Twenty'];

// ---------------------------------------------------------------- palettes
// Soft palettes to match onboarding (WelcomeTutorial): calm page gradient, the
// onboarding button colour + shadow, and softened versions of the four This Month
// colours. fill = postcard, deep = headlines and big figures (large text only),
// label = small text on a card (a shade darker so it stays readable).
export const REVIEW_PALETTES = {
  pink: {
    ui:       { bgTop: '#FAF4FA', bgBottom: '#FCE7F3', button: '#E879A0', shadow: '0 6px 16px -4px rgba(233, 30, 140, 0.4)' },
    income:   { fill: '#FCE3EE', deep: '#E879A0', label: '#C75A85' },
    leftover: { fill: '#EFE7FC', deep: '#9B7FD0', label: '#7B5FB8' },
    saved:    { fill: '#DDF2E5', deep: '#4E9F76', label: '#34815B' },
    spent:    { fill: '#E0EBFB', deep: '#5F8FD6', label: '#3F6FB8' },
  },

  blue: {
    ui:       { bgTop: '#F6F7FF', bgBottom: '#E4E9FF', button: '#4C3BCF', shadow: '0 6px 16px -4px rgba(76, 59, 207, 0.4)' },
    income:   { fill: '#E3E6FD', deep: '#7077E8', label: '#4F55C9' },
    leftover: { fill: '#ECE6FD', deep: '#9A83EC', label: '#7359D0' },
    saved:    { fill: '#D9F1F2', deep: '#3FA7B5', label: '#237F8C' },
    spent:    { fill: '#DDEBFC', deep: '#5A95DD', label: '#3570BA' },
  },

  warm: {
    ui:       { bgTop: '#FBF8F3', bgBottom: '#F3E8D8', button: '#7C5C3A', shadow: '0 6px 16px -4px rgba(124, 92, 58, 0.4)' },
    income:   { fill: '#F6E3D2', deep: '#B8814F', label: '#93602F' },
    leftover: { fill: '#F3E0DC', deep: '#B67E78', label: '#955A54' },
    saved:    { fill: '#E1EBD9', deep: '#6F8C62', label: '#50693F' },
    spent:    { fill: '#F2E7CF', deep: '#B3924F', label: '#8A6C2E' },
  },

  // Purple (admin-only): one lavender family, pink-purple page like the band.
  purple: {
    ui:       { bgTop: '#FBF2FA', bgBottom: '#E8E0FB', button: '#AC8DF2', shadow: '0 6px 16px -4px rgba(140, 100, 220, 0.45)' },
    income:   { fill: '#F5E9FB', deep: '#A55BCB', label: '#8A3FB0' },
    leftover: { fill: '#EFE8FC', deep: '#8F70E4', label: '#6E4FC6' },
    saved:    { fill: '#EAE5FB', deep: '#7A62D4', label: '#5E35A8' },
    spent:    { fill: '#E8E7FC', deep: '#7C7BE0', label: '#5550B8' },
  }
};

export const getReviewPalette = (theme) =>
  isPurpleOn() && theme !== 'warm' && theme !== 'blue'
    ? REVIEW_PALETTES.purple
    : REVIEW_PALETTES[theme === 'warm' ? 'warm' : theme === 'blue' ? 'blue' : 'pink'];

// ---------------------------------------------------------------- dates
export const monthKeyOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

export const shiftMonthKey = (key, delta) => {
  const [y, m] = key.split('-').map(Number);
  return monthKeyOf(new Date(y, m - 1 + delta, 1));
};

export const monthNameOf = (key) => MONTH_NAMES[Number(key.split('-')[1]) - 1];
export const monthShortOf = (key) => MONTH_SHORT[Number(key.split('-')[1]) - 1];
export const yearOf = (key) => Number(key.split('-')[0]);

export const daysInMonthKey = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m, 0).getDate();
};

export const ordinal = (n) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const numberWord = (n) => (n >= 0 && n <= 20 ? NUMBER_WORDS[n] : String(n));

// "Dining out" -> "dining out" for use mid-sentence, but names with their own
// capitals ("Uber Eats", "TTC") are left alone.
export const inSentence = (name) =>
  name && name.slice(1) === name.slice(1).toLowerCase() ? name.charAt(0).toLowerCase() + name.slice(1) : name;

// ---------------------------------------------------------------- totals
const amt = (t) => Number(t.amount) || 0;
const sum = (list) => list.reduce((s, t) => s + amt(t), 0);

export const monthTotals = (transactions, key) => {
  const tx = (transactions || []).filter((t) => t.month_key === key);
  const expenses = tx.filter((t) => !t.is_income && !t.is_savings && !t.is_transfer);
  const income = sum(tx.filter((t) => t.is_income && !t.is_transfer && !t.is_from_savings));
  const savingsTx = tx.filter((t) => t.is_savings);
  const deposits = sum(savingsTx.filter((t) => t.transaction_type !== 'withdrawal'));
  const withdrawals = sum(savingsTx.filter((t) => t.transaction_type === 'withdrawal'));
  const fromSavings = sum(tx.filter((t) => t.is_from_savings));
  const spent = sum(expenses);
  const saved = deposits - withdrawals - fromSavings;
  return { tx, expenses, income, spent, saved, leftover: income - spent - saved };
};

export const categoryLabel = (cat) => {
  const { emoji, name } = parseCategoryEmoji(cat || '');
  return { emoji: emoji || '\u{1F4B8}', name: name || cat || 'Other' };
};

const byCategory = (expenses) => {
  const map = {};
  expenses.forEach((t) => {
    const c = t.category || 'Other';
    if (!map[c]) map[c] = { total: 0, count: 0 };
    map[c].total += amt(t);
    map[c].count += 1;
  });
  return map;
};

const longestRun = (days) => {
  let best = { len: 0, start: 0, end: 0 };
  let start = null;
  let prev = null;
  days.forEach((d) => {
    if (prev !== null && d === prev + 1) {
      // run continues
    } else {
      start = d;
    }
    const len = d - start + 1;
    if (len > best.len) best = { len, start, end: d };
    prev = d;
  });
  return best;
};

const noSpendList = (noSpendDays, key) => {
  const max = daysInMonthKey(key);
  const raw = (noSpendDays && noSpendDays[key]) || [];
  return Array.from(new Set(raw.map(Number).filter((d) => d >= 1 && d <= max))).sort((a, b) => a - b);
};

// ---------------------------------------------------------------- feelings
// "How did it feel?" tags on expenses. Stored on the transaction as `feeling`
// (one of these ids); untagged expenses just don't have the field.
export const FEELINGS = [
  { id: 'needed', emoji: '\u2705', label: 'Needed it' },
  { id: 'worth', emoji: '\u{1F60A}', label: 'Worth it' },
  { id: 'treat', emoji: '\u{1F370}', label: 'Treat' },
  { id: 'impulse', emoji: '\u26A1', label: 'Impulse' },
  { id: 'regret', emoji: '\u{1F615}', label: 'Regret' },
];

export const feelingSummary = (expenses) => {
  const rows = FEELINGS.map((f) => ({ ...f, count: 0, amount: 0 }));
  const byId = {};
  rows.forEach((r) => { byId[r.id] = r; });
  (expenses || []).forEach((t) => {
    const r = byId[t.feeling];
    if (!r) return;
    r.count += 1;
    r.amount += amt(t);
  });
  const tagged = rows.reduce((n, r) => n + r.count, 0);
  const taggedAmount = rows.reduce((n, r) => n + r.amount, 0);
  return { rows, tagged, taggedAmount, total: (expenses || []).length };
};

// ---------------------------------------------------------------- availability
// A month has a review once it's over and has at least one transaction.
export const hasReviewData = (transactions, key) =>
  (transactions || []).some((t) => t.month_key === key);

// For the Home pop-up only: the user has to have been using the app from the
// start of that month (first activity on or before the 7th), so a brand-new
// user doesn't get a half-empty review for their first few days.
export const isReviewEligible = (transactions, key) => {
  if (!hasReviewData(transactions, key)) return false;
  let first = null;
  (transactions || []).forEach((t) => {
    const d = t.date ? new Date(t.date) : null;
    if (d && !isNaN(d) && (!first || d < first)) first = d;
  });
  if (!first) return false;
  const [y, m] = key.split('-').map(Number);
  return first <= new Date(y, m - 1, 7, 23, 59, 59);
};

// ---------------------------------------------------------------- review state
export const getReviewState = (userData) => (userData && userData.month_end_review) || {};

export const markReviewSeen = (userData, saveUserData, key) => {
  const s = getReviewState(userData);
  if (s.seen && s.seen[key]) return;
  saveUserData({ month_end_review: { ...s, seen: { ...(s.seen || {}), [key]: new Date().toISOString() } } });
};

export const skipReview = (userData, saveUserData, key) => {
  const s = getReviewState(userData);
  saveUserData({ month_end_review: { ...s, skipped: { ...(s.skipped || {}), [key]: true } } });
};

// Adds the note as the first line of that month's budget notes (Setup > Notes),
// which store one newline-joined string per month.
export const pinNoteToMonth = (userData, saveUserData, key, text) => {
  const clean = (text || '').trim();
  if (!clean) return;
  const bs = (userData && userData.budget_settings) || {};
  const notes = { ...(bs.notes_by_month || {}) };
  const existing = (notes[key] || '').trim();
  notes[key] = existing ? `${clean}\n${existing}` : clean;
  saveUserData({ budget_settings: { ...bs, notes_by_month: notes } });
};

// ---------------------------------------------------------------- the review
export const buildMonthReview = ({
  monthKey,
  transactions = [],
  noSpendDays = {},
  sinkingFunds = [],
  categoryBudgets = {},
}) => {
  const cur = monthTotals(transactions, monthKey);
  const prevKey = shiftMonthKey(monthKey, -1);
  const nextKey = shiftMonthKey(monthKey, 1);
  const prev = monthTotals(transactions, prevKey);
  const hasPrev = prev.tx.length > 0;

  // --- saved vs earlier months
  let bestSince = null; // month key of the last month that beat this one
  let bestEver = false;
  if (cur.saved > 0) {
    const earliest = (transactions || []).reduce((m, t) => (t.month_key && (!m || t.month_key < m) ? t.month_key : m), null);
    let monthsWithData = 0;
    let beaten = false;
    for (let k = 1; k <= 36; k++) {
      const key = shiftMonthKey(monthKey, -k);
      if (!earliest || key < earliest) break;
      const t = monthTotals(transactions, key);
      if (!t.tx.length) continue;
      monthsWithData += 1;
      if (t.saved >= cur.saved) {
        beaten = true;
        if (k >= 3) bestSince = key;
        break;
      }
    }
    if (!beaten && monthsWithData >= 2) bestEver = true;
  }

  // --- no-spend days
  const nsDays = noSpendList(noSpendDays, monthKey);
  const nsPrev = noSpendList(noSpendDays, prevKey).length;
  const run = longestRun(nsDays);

  // --- category changes
  const curCats = byCategory(cur.expenses);
  const prevCats = byCategory(prev.expenses);
  let drop = null;
  let rise = null;
  if (hasPrev) {
    const cats = new Set([...Object.keys(curCats), ...Object.keys(prevCats)]);
    cats.forEach((c) => {
      const now = (curCats[c] && curCats[c].total) || 0;
      const was = (prevCats[c] && prevCats[c].total) || 0;
      const diff = now - was;
      const row = {
        category: c,
        ...categoryLabel(c),
        now,
        was,
        diff,
        pct: was > 0 ? Math.round((Math.abs(diff) / was) * 100) : null,
        countNow: (curCats[c] && curCats[c].count) || 0,
        countWas: (prevCats[c] && prevCats[c].count) || 0,
      };
      // Only changes worth a slide: at least $10 and at least 5%.
      const big = Math.abs(diff) >= 10 && (was === 0 || Math.abs(diff) / was >= 0.05);
      if (!big) return;
      if (diff < 0 && was > 0 && (!drop || diff < drop.diff)) drop = row;
      if (diff > 0 && (!rise || diff > rise.diff)) rise = row;
    });
  }
  if (rise) {
    const budget = Number(((categoryBudgets || {})[monthKey] || {})[rise.category]) || 0;
    rise.budget = budget;
  }
  const biggestMove = drop && rise ? (Math.abs(drop.diff) >= rise.diff ? 'drop' : 'rise') : drop ? 'drop' : rise ? 'rise' : null;

  // --- biggest day
  const [my, mm] = monthKey.split('-').map(Number);
  const dayMap = {};
  cur.expenses.forEach((t) => {
    if (t.is_recurring) return; // rent and bills aren't a "big day"
    const d = t.date ? new Date(t.date) : null;
    if (!d || isNaN(d) || d.getFullYear() !== my || d.getMonth() !== mm - 1) return;
    const n = d.getDate();
    if (!dayMap[n]) dayMap[n] = { total: 0, count: 0, cats: {} };
    dayMap[n].total += amt(t);
    dayMap[n].count += 1;
    const c = t.category || 'Other';
    dayMap[n].cats[c] = (dayMap[n].cats[c] || 0) + amt(t);
  });
  let biggestDay = null;
  Object.entries(dayMap).forEach(([n, v]) => {
    if (v.count < 2) return; // one purchase isn't much of a day
    if (!biggestDay || v.total > biggestDay.total) biggestDay = { day: Number(n), ...v };
  });
  // Lots of transactions logged on one date that add up to most of the month
  // looks like a catch-up entry, not a real shopping day - leave that slide out.
  if (biggestDay && biggestDay.count >= 10 && biggestDay.total >= cur.spent * 0.6) biggestDay = null;
  if (biggestDay) {
    const date = new Date(my, mm - 1, biggestDay.day);
    biggestDay.weekday = WEEKDAYS[date.getDay()];
    biggestDay.isWeekend = date.getDay() === 0 || date.getDay() === 6;
    biggestDay.top = Object.entries(biggestDay.cats)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([c, v]) => ({ category: c, ...categoryLabel(c), total: v }));
    const topCat = biggestDay.top[0];
    biggestDay.mostly = topCat && topCat.total >= biggestDay.total * 0.5 ? topCat.name : null;
  }
  const expenseCount = cur.expenses.length;
  const feelings = feelingSummary(cur.expenses);
  const avgBuy = expenseCount ? cur.spent / expenseCount : 0;

  // --- funds
  const funds = (sinkingFunds || []).map((f) => {
    const mine = transactions.filter((t) => t.account_id === f.id && t.account_type === 'fund');
    const inMonth = mine.filter((t) => t.month_key === monthKey);
    const net = sum(inMonth.filter((t) => t.transaction_type === 'deposit')) - sum(inMonth.filter((t) => t.transaction_type === 'withdrawal'));
    const upTo = mine.filter((t) => t.month_key && t.month_key <= monthKey);
    const interest = sum(transactions.filter((t) => t.is_interest && t.interest_account_id === f.id && t.interest_account_type === 'fund' && t.month_key && t.month_key <= monthKey));
    const balance = (Number(f.starting_balance) || 0)
      + sum(upTo.filter((t) => t.transaction_type === 'deposit'))
      - sum(upTo.filter((t) => t.transaction_type === 'withdrawal'))
      + interest;
    return { id: f.id, name: (f.name || 'Fund').replace(/ Fund$/, ''), emoji: f.emoji || '\u{1F4B0}', goal: Number(f.goal) || 0, net, balance };
  }).filter((f) => f.net > 0).sort((a, b) => b.net - a.net);
  const fundsTotal = funds.reduce((s, f) => s + f.net, 0);
  const invTx = cur.tx.filter((t) => t.account_type === 'investment' && t.is_savings);
  const invested = sum(invTx.filter((t) => t.transaction_type !== 'withdrawal')) - sum(invTx.filter((t) => t.transaction_type === 'withdrawal'));
  let projection = null;
  const lead = funds.find((f) => f.goal > f.balance && f.net > 0);
  if (lead) {
    const months = Math.ceil((lead.goal - lead.balance) / lead.net);
    if (months > 0 && months <= 60) projection = { name: lead.name, key: shiftMonthKey(monthKey, months) };
  }

  return {
    monthKey,
    prevKey,
    nextKey,
    monthName: monthNameOf(monthKey),
    prevName: monthNameOf(prevKey),
    nextName: monthNameOf(nextKey),
    year: yearOf(monthKey),
    days: daysInMonthKey(monthKey),
    txCount: cur.tx.length,
    income: cur.income,
    spent: cur.spent,
    saved: cur.saved,
    leftover: cur.leftover,
    hasPrev,
    prevSaved: prev.saved,
    savedDiff: hasPrev ? cur.saved - prev.saved : null,
    bestSince,
    bestEver,
    noSpend: { count: nsDays.length, days: nsDays, prevCount: nsPrev, run },
    drop,
    rise,
    biggestMove,
    biggestDay,
    expenseCount,
    avgBuy,
    funds: funds.slice(0, 3),
    fundCount: funds.length,
    fundsTotal,
    invested,
    projection,
    feelings,
  };
};

// Short summary for the list in 2026 Stats - cheap, no comparisons.
export const monthSummary = (transactions, noSpendDays, key) => {
  const t = monthTotals(transactions, key);
  return { saved: t.saved, spent: t.spent, noSpend: noSpendList(noSpendDays, key).length };
};

// Fraction phrase for "You kept ___ of what came in!"
export const keptPhrase = (saved, income) => {
  if (!(income > 0) || !(saved > 0)) return null;
  const p = saved / income;
  if (Math.abs(p - 0.5) <= 0.03) return 'half';
  if (Math.abs(p - 1 / 3) <= 0.03) return 'a third';
  if (Math.abs(p - 0.25) <= 0.03) return 'a quarter';
  if (Math.abs(p - 0.2) <= 0.02) return 'a fifth';
  if (Math.abs(p - 0.1) <= 0.015) return 'a tenth';
  return `${Math.round(p * 100)}%`;
};
